import {
  VideoRecorder,
  VideoRecorderProps,
  VideoRecording,
} from "@amzn/itsvideo-recorder";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { RefObject, useContext, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Logger } from "@/apis/Logger";
import {
  IdStillCaptureOutcome,
  Metrics,
  publishIdStillCaptureOutcome,
  publishIdStillDecision,
} from "@/apis/Metrics";
import SubmitVideoButton from "@/components/Commons/SubmitVideoButton";
import { RefPageValue } from "@/components/GetSupport/ChatWithITSupport/types";
import { SnowplowTracker } from "@/helpers/snowplow";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  useFeatureFlag,
  useFeatureFlagsContext,
} from "@/stores/contexts/FeatureFlagsContext";
import { Day1EventCategory } from "@/types/snowplow-events";

import ConfirmIdImage from "./ConfirmIdImage";
import RecorderError from "./RecorderError";

type VideoRecorderCustomProps = Omit<
  VideoRecorderProps,
  "onRecord" | "onRetry" | "children" | "retryI18nId" | "resolutionProfile"
> & {
  /*** The Snowplow event category ***/
  eventCategory: Day1EventCategory;

  /**
   * For full remote ID verification only (FRIV):
   * Callback used to navigate the client back to the main ID verification steps on clicking
   * the button to submit for review at the end of each record step
   */
  clearSelectedStep?: () => void;

  /**
   * The reference to the FullIdVerification container element used to calculate the window
   * coordinate to scroll back to after the client completes ID recording steps.
   */
  fullIdVerificationElementRef?: RefObject<HTMLDivElement>;

  /**
   * Who mounted this component? Used for metrics collection on sub components.
   */
  refPage?: RefPageValue;
};

const VIDEO_RECORDER_ERROR_PUBLISHER =
  Metrics.publisherInstance.newChildActionPublisherForMethod(
    "ManualIdvVideoRecorder"
  );

export default function VideoRecorderCustom(
  props: VideoRecorderCustomProps
): JSX.Element {
  const { isLoading } = useFeatureFlagsContext();
  const is1080pCapEnabled = useFeatureFlag("cameraResolution1080pCap", true);
  // Defaults off so ID still capture ships dark until the backend flag exists
  // (NHE-6565); a backend flag value overrides this default when present.
  const isIdStillCaptureEnabled = useFeatureFlag(
    "idStillCaptureEnabled",
    false
  );

  if (isLoading) {
    return (
      <div data-test-id="videoRecorderDataTestId">
        <Spinner data-test-id="videoRecorderFlagsLoadingSpinner" />
      </div>
    );
  }

  return (
    <VideoRecorderCustomInner
      {...props}
      initialIs1080pCap={is1080pCapEnabled}
      initialIdStillCapture={isIdStillCaptureEnabled}
    />
  );
}

function VideoRecorderCustomInner({
  initialIs1080pCap,
  initialIdStillCapture,
  ...props
}: VideoRecorderCustomProps & {
  initialIs1080pCap: boolean;
  initialIdStillCapture: boolean;
}): JSX.Element {
  /**
   * Freeze the experiment assignment at mount: react-video-recorder does not restart an
   * active stream when its constraints prop changes, so a mid-session flag refetch flipping
   * the value would desynchronize the logged profile from the actual capture. Freezing
   * guarantees logged assignment === applied constraints for the whole recorder lifetime.
   */
  const [resolutionProfile] = useState<"1080p" | "max">(
    initialIs1080pCap ? "1080p" : "max"
  );
  // Freeze the capture flag at mount for the same reason as the resolution
  // profile: the recorder can't restart its capture pipeline mid-session.
  const [idStillCaptureEnabled] = useState(initialIdStillCapture);
  // The screen opts in via enableDocumentCapture (primary/secondary ID only);
  // the flag gates it globally. Both must be true to capture a still.
  const enableDocumentCapture =
    idStillCaptureEnabled && Boolean(props.enableDocumentCapture);
  const { state, dispatch } = useContext(AppContext);

  // Whether the new hire has confirmed the captured still for the current
  // recording. Used to attribute a subsequent retry as a still-rejection vs a
  // post-accept re-record. A ref (not state) since it only gates side effects.
  const stillConfirmedRef = useRef(false);
  const documentType = props.documentType;
  const currentDoc = () =>
    state.documents?.find((doc) => doc.documentType === documentType);

  const retryRecording = () => {
    // If capture is on and the current step has an unconfirmed still, this
    // retry is the new hire rejecting that still — record it before popping.
    if (enableDocumentCapture && !stillConfirmedRef.current) {
      const doc = currentDoc();
      if (doc?.idImageBlob) {
        publishIdStillDecision({
          documentType,
          decision: "retried",
          source: doc.idImageCaptureSource ?? "auto",
        });
      }
    }
    dispatch({ type: ActionType.POP_DOCUMENT });
  };

  const saveRecording = (videoRecording: VideoRecording) => {
    dispatch({
      type: ActionType.ADD_DOCUMENT,
      document: { ...videoRecording, submitClicked: false },
    });
    // A fresh recording resets the confirm state for this step.
    stillConfirmedRef.current = false;
    if (enableDocumentCapture) {
      const outcome: IdStillCaptureOutcome = videoRecording.idImageBlob
        ? videoRecording.idImageCaptureSource ?? "auto"
        : "none";
      publishIdStillCaptureOutcome({ documentType, outcome });
    }
  };

  // Called when the new hire confirms the still is legible (from ConfirmIdImage).
  const handleStillConfirmed = () => {
    stillConfirmedRef.current = true;
    const doc = currentDoc();
    if (doc?.idImageBlob) {
      publishIdStillDecision({
        documentType,
        decision: "accepted",
        source: doc.idImageCaptureSource ?? "auto",
      });
    }
  };

  const intl = useIntl();

  /**
   * Emit error metrics and error logs generated from the react-video-recorder.
   * @param error An Error instance
   */
  /* istanbul ignore next reason: Video recording cannot be tested currently */
  const handleError = (error: Error) => {
    void Logger.fatal(
      "Unable to complete video recording due to some errors.",
      error
    );
    VIDEO_RECORDER_ERROR_PUBLISHER.publishCounterMonitor(
      "VideoRecorderError",
      1
    );
  };

  return (
    <div data-test-id="videoRecorderDataTestId">
      <VideoRecorder
        retryI18nId={"identityCheck-button-retry"}
        onRecord={saveRecording}
        onRetry={retryRecording}
        snowplowTracker={SnowplowTracker.instance}
        katalLogger={Logger.instance}
        onError={handleError}
        onErrorRenderer={(error: Error) => <RecorderError error={error} />}
        noMovementText={intl.formatMessage({
          id: "videoRecorder-noMovementText",
        })}
        personId={SnowplowTracker.userId}
        alias={state.amazonUsername}
        {...props}
        resolutionProfile={resolutionProfile}
        enableDocumentCapture={enableDocumentCapture}
        documentCaptureStatusText={
          enableDocumentCapture
            ? {
                moveCloser: intl.formatMessage({
                  id: "videoRecorder-documentCapture-moveCloser",
                }),
                holdStill: intl.formatMessage({
                  id: "videoRecorder-documentCapture-holdStill",
                }),
                captured: intl.formatMessage({
                  id: "videoRecorder-documentCapture-captured",
                }),
              }
            : undefined
        }
      >
        <ConfirmIdImage
          documentType={props.documentType}
          enabled={enableDocumentCapture}
          onConfirm={handleStillConfirmed}
        >
          <SubmitVideoButton
            eventCategory={props.eventCategory}
            clearSelectedStep={props.clearSelectedStep}
            fullIdVerificationElementRef={props.fullIdVerificationElementRef}
            refPage={props.refPage}
          />
        </ConfirmIdImage>
      </VideoRecorder>
    </div>
  );
}
